import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, PauseCircle, PlayCircle, Trash, PlusCircle, PlugsConnected, Warning, CheckCircle, Users, ChatCircleText, XCircle } from "@phosphor-icons/react";

export default function EmailAutomation() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [config, setConfig] = useState(null);
  const [connection, setConnection] = useState(null);
  const [filter, setFilter] = useState("");
  const [sequenceFilter, setSequenceFilter] = useState(params.get("sequence") || "");

  const load = async () => {
    const [s, e, c1, c2] = await Promise.all([
      api.get("/email/automation/summary"),
      api.get("/email/enrollments", { params: { status: filter || undefined, sequence_id: sequenceFilter || undefined } }),
      api.get("/email/config"),
      api.get("/email/connection"),
    ]);
    setSummary(s.data);
    setEnrollments(e.data.enrollments || []);
    setConfig(c1.data);
    setConnection(c2.data.connection);
  };

  useEffect(() => { load(); }, [filter, sequenceFilter]);

  const isDevMode = config?.mode === "dev" && !config?.branded_sender;
  const isConnected = connection?.status === "active";

  const pause = async (id) => {
    try { await api.post(`/email/enrollments/${id}/pause`); toast.success("Paused"); load(); }
    catch { toast.error("Failed to pause"); }
  };
  const resume = async (id) => {
    try { await api.post(`/email/enrollments/${id}/resume`); toast.success("Resumed"); load(); }
    catch { toast.error("Failed to resume"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this contact from the sequence? No more emails will send.")) return;
    try { await api.delete(`/email/enrollments/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed to remove"); }
  };

  const toggleSequenceActive = async (seq) => {
    try {
      await api.put(`/email/sequences/${seq.id}`, { active: !seq.active });
      toast.success(!seq.active ? "Sequence resumed" : "Sequence paused");
      load();
    } catch { toast.error("Failed"); }
  };
  const deleteSequence = async (seq) => {
    if (!window.confirm(`Delete "${seq.name}"? Active enrollments will be removed.`)) return;
    try { await api.delete(`/email/sequences/${seq.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const grouped = useMemo(() => {
    const groups = { active: [], paused: [], replied: [], completed: [], unsubscribed: [], bounced: [], removed: [] };
    for (const e of enrollments) (groups[e.status] || groups.active).push(e);
    return groups;
  }, [enrollments]);

  return (
    <div className="p-6 md:p-10" data-testid="email-automation-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">// email automation</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Follow-ups on autopilot</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to="/dashboard/email-setup" className="btn-primary flex items-center gap-2" data-testid="new-sequence-btn"><PlusCircle weight="bold" /> New sequence</Link>
        </div>
      </div>

      {/* Connection banner */}
      {!isConnected && (
        <div className="mb-6 p-4 border flex items-start gap-3" style={{ borderColor: "#FF9500", background: "rgba(255,149,0,0.06)" }} data-testid="no-connection-banner">
          <Warning weight="fill" style={{ color: "#FF9500", flexShrink: 0, marginTop: 2 }} />
          <div className="flex-1">
            <div className="font-semibold">No mailbox connected</div>
            <div className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Connect Gmail (or enable Demo Mode) to start sending sequences.</div>
          </div>
          <Link to="/dashboard/email-setup" className="btn-secondary">Connect</Link>
        </div>
      )}

      {isDevMode && isConnected && (
        <div className="mb-6 p-4 border flex items-start gap-3 text-sm" style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.05)" }} data-testid="dev-mode-banner">
          <PlugsConnected weight="fill" style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />
          <div>
            <b>Demo Mode active</b> — emails send from a MyNexusCard-owned sender labeled <code>[DEMO]</code>. Add real Nylas credentials to send from your Gmail.
          </div>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
        <MetricCell label="Active" value={summary?.counts?.active_enrollments} testid="metric-active" />
        <MetricCell label="Paused" value={summary?.counts?.paused_enrollments} testid="metric-paused" />
        <MetricCell label="Replied" value={summary?.counts?.replied_enrollments} accent testid="metric-replied" />
        <MetricCell label="Completed" value={summary?.counts?.completed_enrollments} testid="metric-completed" />
        <MetricCell label="Sent 30d" value={summary?.counts?.sent_30d} testid="metric-sent-30d" />
        <MetricCell label="Failed 30d" value={summary?.counts?.failed_30d} warn={summary?.counts?.failed_30d > 0} testid="metric-failed-30d" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
        {/* Sequences */}
        <section className="lg:col-span-5">
          <div className="overline mb-4">// sequences</div>
          <div className="border" style={{ borderColor: "var(--border)" }}>
            {(summary?.sequences?.length || 0) === 0 && (
              <div className="p-6 text-sm" style={{ color: "var(--text-2)" }}>No sequences yet. <Link to="/dashboard/email-setup" className="underline" style={{ color: "var(--accent)" }}>Create your first →</Link></div>
            )}
            {(summary?.sequences || []).map((s, i) => (
              <div key={s.id} className={`p-4 ${i > 0 ? "border-t" : ""}`} style={{ borderColor: "var(--border)" }} data-testid={`sequence-row-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold truncate">{s.name}</div>
                      {s.auto_enroll_new_contacts && <span className="tag" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>AUTO</span>}
                      {!s.active && <span className="tag">PAUSED</span>}
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--text-3)" }}>
                      {s.step_count} steps · {s.active_enrollments} active · {s.completed_enrollments} completed
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <button onClick={() => setSequenceFilter(s.id)} className="btn-secondary text-xs px-3 py-1" data-testid={`filter-sequence-${s.id}`}>View</button>
                    <button onClick={() => toggleSequenceActive(s)} className="btn-secondary text-xs px-3 py-1" data-testid={`toggle-sequence-${s.id}`}>
                      {s.active ? "Pause" : "Resume"}
                    </button>
                    <button onClick={() => deleteSequence(s)} className="btn-secondary text-xs px-3 py-1" data-testid={`delete-sequence-${s.id}`}>
                      <Trash weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Enrollments */}
        <section className="lg:col-span-7">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="overline">// enrollments</div>
            <div className="flex gap-2 flex-wrap items-center">
              {sequenceFilter && (
                <button onClick={() => setSequenceFilter("")} className="tag">
                  Sequence filter <XCircle weight="bold" style={{ marginLeft: 4 }} />
                </button>
              )}
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="input-base"
                style={{ width: 180 }}
                data-testid="enrollment-status-filter"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="replied">Replied</option>
                <option value="completed">Completed</option>
                <option value="unsubscribed">Unsubscribed</option>
                <option value="bounced">Bounced</option>
                <option value="removed">Removed</option>
              </select>
            </div>
          </div>

          <div className="border" style={{ borderColor: "var(--border)" }}>
            {enrollments.length === 0 && (
              <div className="p-6 text-sm" style={{ color: "var(--text-2)" }}>Nothing enrolled yet. New contacts from scans / referrals / CSV imports will land here automatically if you enabled auto-enroll.</div>
            )}
            {enrollments.map((e, i) => (
              <div key={e.id} className={`p-4 ${i > 0 ? "border-t" : ""}`} style={{ borderColor: "var(--border)" }} data-testid={`enrollment-row-${e.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold truncate">{e.contact?.name || e.contact_email}</div>
                      <StatusBadge status={e.status} />
                      {e.paused_reason && <span className="tag">reason: {e.paused_reason}</span>}
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--text-3)" }}>
                      {e.contact?.email} {e.contact?.company ? `· ${e.contact.company}` : ""}
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--text-3)" }}>
                      Step {(e.current_step_index || 0) + 1} · Next: {e.next_send_at ? new Date(e.next_send_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {e.status === "active" && (
                      <button onClick={() => pause(e.id)} className="btn-secondary text-xs px-3 py-1 flex items-center gap-1" data-testid={`pause-${e.id}`}>
                        <PauseCircle weight="bold" /> Pause
                      </button>
                    )}
                    {(e.status === "paused" || e.status === "replied") && (
                      <button onClick={() => resume(e.id)} className="btn-secondary text-xs px-3 py-1 flex items-center gap-1" data-testid={`resume-${e.id}`}>
                        <PlayCircle weight="bold" /> Resume
                      </button>
                    )}
                    {e.status !== "removed" && (
                      <button onClick={() => remove(e.id)} className="btn-secondary text-xs px-3 py-1" data-testid={`remove-${e.id}`}>
                        <Trash weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Recent events */}
          <div className="overline mt-10 mb-4">// recent send activity</div>
          <div className="border" style={{ borderColor: "var(--border)" }}>
            {(summary?.recent_events?.length || 0) === 0 && (
              <div className="p-6 text-sm" style={{ color: "var(--text-2)" }}>No sends yet.</div>
            )}
            {(summary?.recent_events || []).map((ev, i) => (
              <div key={ev.id} className={`p-3 flex items-center justify-between text-sm ${i > 0 ? "border-t" : ""}`} style={{ borderColor: "var(--border)" }}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {ev.status === "sent" ? (
                      <CheckCircle weight="fill" style={{ color: "var(--accent)" }} />
                    ) : (
                      <XCircle weight="fill" style={{ color: "#FF3B30" }} />
                    )}
                    <span className="truncate">{ev.subject || "—"}</span>
                    {ev.mode === "dev" && <span className="tag" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>DEMO</span>}
                  </div>
                  {ev.error && <div className="text-xs mt-1" style={{ color: "#FF3B30" }}>{ev.error}</div>}
                </div>
                <div className="text-xs whitespace-nowrap ml-3" style={{ color: "var(--text-3)" }}>
                  {new Date(ev.created_at || ev.sent_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function MetricCell({ label, value, accent, warn, testid }) {
  return (
    <div className="border-r border-b p-4" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-2xl md:text-3xl mt-1" style={{ color: warn ? "#FF3B30" : accent ? "var(--accent)" : "#fff" }}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { color: "var(--accent)", label: "ACTIVE" },
    paused: { color: "#FF9500", label: "PAUSED" },
    replied: { color: "#34C759", label: "REPLIED" },
    completed: { color: "var(--text-3)", label: "DONE" },
    unsubscribed: { color: "#FF3B30", label: "UNSUB" },
    bounced: { color: "#FF3B30", label: "BOUNCED" },
    removed: { color: "var(--text-3)", label: "REMOVED" },
  };
  const m = map[status] || { color: "var(--text-3)", label: status?.toUpperCase() };
  return <span className="tag" style={{ color: m.color, borderColor: m.color }}>{m.label}</span>;
}
